import { Container, Sprite, Assets, type DestroyOptions } from 'pixi.js';
import type { PageSide } from '@/types';
import watermarkUrl from '@/assets/watermark/ai.png';

/** 水印定位的目标区域（原点为区域中心，与 PixiJS 坐标系一致） */
export interface WatermarkRegion {
  width: number;
  height: number;
  x: number;
  y: number;
}

export class Watermark extends Container {
  private sprite: Sprite | null = null;

  private _loaded = false;

  /** 缓存 resize 参数，确保精灵异步加载完成后也能正确应用位置 */
  private _pendingResize: {
    region: WatermarkRegion;
    side?: PageSide;
    displayScale?: number;
  } | null = null;

  constructor() {
    super();
    this.label = 'watermark';
    this.alpha = 0.7;
    this.scale.set(0.7);
    this.load();
  }

  public async load(): Promise<void> {
    try {
      const texture = await Assets.load(watermarkUrl);
      this.sprite = new Sprite({ texture });
      this.addChild(this.sprite);
      this._loaded = true;
      this.applyPendingResize();
    } catch (error) {
      console.error('Watermark load failed:', error);
    }
  }

  /**
   * 根据目标区域与 pageSide 将水印放置到对应角落，距离边距 20px
   * - left:  区域左下角
   * - right: 区域右上角（默认）
   */
  public resize(region: WatermarkRegion, side?: PageSide, displayScale = 1): void {
    this._pendingResize = { region, side, displayScale };
    this.applyPendingResize();
  }

  private applyPendingResize(): void {
    if (!this.sprite || !this._pendingResize) return;

    const { region, side, displayScale = 1 } = this._pendingResize;
    const { x, y, width, height } = region;
    const padding = 20 * displayScale;

    this.scale.set(0.7 * displayScale);

    if (side === 'left') {
      this.sprite.anchor.set(0, 1);
      this.position.set(x - width / 2 + padding, y + height / 2 - padding);
    } else {
      this.sprite.anchor.set(1, 0);
      this.position.set(x + width / 2 - padding, y - height / 2 + padding);
    }
  }

  public get loaded(): boolean {
    return this._loaded;
  }

  public override destroy(options?: DestroyOptions): void {
    this.sprite?.destroy();
    this.sprite = null;
    super.destroy(options);
  }
}
